/**
 * Layout â€” top-level dashboard chrome.
 *
 * Delegates the visual shell to {@link WorkspaceShell}. Legacy left-sidebar
 * chrome lives in `src/_deadcode/LegacySidebarLayout/` (not imported).
 *
 * Every caller uses `import Layout from '.../Layout'` with `currentPage`,
 * `onNavigate`, and `onSignOut` â€” contracts used by AppRouter and
 * DashboardContainer must stay stable.
 *
 * Layout owns auth-/data-bound concerns: profile loading, role detection,
 * plan/credits, logout + credit modals, editor-mode detection, reminder sync.
 */
import {
  Bell,
  Sun,
  Moon,
  Coins,
} from 'lucide-react';
import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { Capacitor } from '@capacitor/core';

import { CreditPurchaseModal } from '../../../components/CreditSystem';
import { goToCreditCheckout, openCreditPurchase } from '../../../utils/creditCheckoutNavigation';
import { LanguageSelector } from '../../../components/LanguageSelector';
import LogoutDialog from '../../../components/LogoutDialog';
import { useTheme } from '../../../contexts/ThemeContext';
import { usePlanStatus } from '../../../hooks/usePlanStatus';
import { supabase } from '../../../lib/supabase';
import { syncCalendarReminderNotifications } from '../../../services/calendarRemindersService';
import {
  clearAllUserNotifications,
  getNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  syncTaskReminderNotifications,
  NOTIFICATIONS_REFRESH_EVENT,
} from '../../../services/objectivesTasksService';
import type { UserNotification } from '../../../types/objectivesTasks';
import { appLogger } from '../../../utils/appLogger';
import { getCurrentUserId } from '../../../utils/authUtils';
import WorkspaceShell from '../../shell/WorkspaceShell';
import { isOwnerAdminEmail } from './Jobs/helpers';

export const PROFILE_INITIAL_TAB_KEY = 'profile_initial_tab';
export const PROFILE_NAVIGATE_TAB_EVENT = 'profile-navigate-tab';

export type DashboardPage =
  | 'dashboard'
  | 'journey-setup'
  | 'single-optimize'
  | 'history'
  | 'resume-builder'
  | 'payment-history'
  | 'prepare-for-interview'
  | 'jobs'
  | 'jobs-admin'
  | 'collaborate'
  | 'ai-companion'
  | 'agent'
  | 'calendar-notes'
  | 'support'
  | 'product-feedback'
  | 'profile'
  | 'account-settings'
  | 'credits'
  | 'developer'
  | 'skill-assessment'
  | 'blog-cms'
  | 'blog-editor'
  | 'control-panel'
  | 'cron-jobs-admin'
  | 'testimonials-admin'
  | 'translations-admin'
  | 'referrals';

interface LayoutProps {
  children: React.ReactNode;
  currentPage: DashboardPage;
  onNavigate: (page: DashboardPage) => void;
  onSignOut: () => void | Promise<void>;
  /**
   * When false, main content renders edge-to-edge (used by /agent so the
   * Copilot canvas is not inset like dashboard tiles).
   */
  mainContentPadded?: boolean;
  /** Hero search submission (dashboard / jobs greeting strip) â€” stays separate from the AI Agent route. */
  onHeroSearch?: (trimmedQuery: string) => void;
}

// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// Editor-mode utility strip building blocks
// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

function ThemeToggleButton({ className }: { className?: string }) {
  const { t } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  return (
    <button
      type="button"
      onClick={toggleTheme}
      className={`min-h-[44px] min-w-[44px] flex items-center justify-center p-2 rounded-lg vscode-bg-tertiary hover:vscode-bg-hover transition-colors touch-manipulation ${className ?? ''}`}
      aria-label={theme === 'dark' ? t('layout.ariaSwitchToLight') : t('layout.ariaSwitchToDark')}
    >
      {theme === 'dark' ? (
        <Sun className="w-5 h-5 text-gray-500 dark:text-[#E5E0DF]" />
      ) : (
        <Moon className="w-5 h-5 text-gray-500 dark:text-[#E5E0DF]" />
      )}
    </button>
  );
}


/** Notification tray button (top-right): opens dropdown with list. */
function NotificationTrayButton({ onNavigate }: { onNavigate: (page: DashboardPage) => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<UserNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [clearing, setClearing] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const refreshUnreadCount = useCallback(() => {
    getCurrentUserId().then((uid) => {
      if (!uid) return;
      getNotifications(uid, { limit: 50 }).then((list) => {
        setUnreadCount(list.filter((n) => !n.is_read).length);
      });
    });
  }, []);

  useEffect(() => {
    refreshUnreadCount();
    const poll = window.setInterval(() => refreshUnreadCount(), 90_000);
    return () => window.clearInterval(poll);
  }, [refreshUnreadCount]);

  useEffect(() => {
    const onRefresh = () => refreshUnreadCount();
    window.addEventListener(NOTIFICATIONS_REFRESH_EVENT, onRefresh);
    return () => window.removeEventListener(NOTIFICATIONS_REFRESH_EVENT, onRefresh);
  }, [refreshUnreadCount]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let mounted = true;
    setLoading(true);
    getCurrentUserId()
      .then((userId) => (userId ? getNotifications(userId, { limit: 20 }) : []))
      .then((list) => {
        if (!mounted) return;
        setNotifications(list);
        setUnreadCount(list.filter((n) => !n.is_read).length);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => { mounted = false; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onRefresh = () => {
      getCurrentUserId()
        .then((uid) => (uid ? getNotifications(uid, { limit: 20 }) : []))
        .then((list) => {
          setNotifications(list);
          setUnreadCount(list.filter((n) => !n.is_read).length);
        });
    };
    window.addEventListener(NOTIFICATIONS_REFRESH_EVENT, onRefresh);
    return () => window.removeEventListener(NOTIFICATIONS_REFRESH_EVENT, onRefresh);
  }, [open]);

  const handleMarkRead = (userId: string | null, id: string) => {
    if (!userId) return;
    markNotificationRead(userId, id).then(() => {
      setNotifications((prev) => {
        const updated = prev.map((n) => (n.id === id ? { ...n, is_read: true } : n));
        setUnreadCount(updated.filter((n) => !n.is_read).length);
        return updated;
      });
    });
  };

  const handleMarkAllRead = () => {
    getCurrentUserId().then((userId) => {
      if (!userId) return;
      markAllNotificationsRead(userId).then(() => {
        setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
        setUnreadCount(0);
      });
    });
  };

  const hasTrayItems = notifications.length > 0;

  const handleNotificationClick = (n: UserNotification) => {
    const isBlog =
      n.type === 'blog_draft_created' ||
      n.type === 'blog_draft_stale_reminder' ||
      n.type === 'blog_auto_published';
    const isDailyBrief = n.type === 'daily_brief';

    if (!isBlog && !isDailyBrief) return;

    void getCurrentUserId().then((uid) => {
      if (uid && !n.is_read) void markNotificationRead(uid, n.id);
    });

    if (isBlog) {
      window.history.pushState({}, '', '/admin/blogs');
      window.dispatchEvent(new PopStateEvent('popstate'));
      onNavigate('blog-cms');
    } else {
      window.history.pushState({}, '', '/jobs');
      window.dispatchEvent(new PopStateEvent('popstate'));
      onNavigate('jobs');
    }
    setOpen(false);
  };

  const handleClearAll = () => {
    setClearing(true);
    getCurrentUserId()
      .then((userId) => (userId ? clearAllUserNotifications(userId) : undefined))
      .then(() => {
        setNotifications([]);
        setUnreadCount(0);
      })
      .finally(() => setClearing(false));
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="min-h-[44px] min-w-[44px] flex items-center justify-center p-2 rounded-lg vscode-bg-tertiary hover:vscode-bg-hover transition-colors relative touch-manipulation"
        aria-label={t('layout.notifications')}
      >
        <Bell className="w-5 h-5 text-gray-500 dark:text-[#E5E0DF]" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[1.25rem] h-5 px-1 flex items-center justify-center rounded-full text-xs font-medium bg-[var(--vscode-error)] text-white">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>
      {open && (
        <div className="ds-notification-tray absolute right-0 mt-2 w-80 max-h-[70vh] flex flex-col bg-white dark:bg-[var(--vscode-bg-secondary)] border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl z-[1002] overflow-hidden">
          <div className="p-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between shrink-0" style={{ background: 'var(--brand-popup-header)' }}>
            <p className="text-sm font-semibold text-white">{t('layout.notifications')}</p>
            {(unreadCount > 0 || hasTrayItems) && (
              <div className="flex items-center gap-2 shrink-0">
                {unreadCount > 0 && (
                  <button
                    type="button"
                    onClick={handleMarkAllRead}
                    disabled={clearing}
                    className="min-h-[44px] px-2 text-xs text-white/80 hover:text-white hover:underline touch-manipulation disabled:opacity-50"
                  >
                    {t('layout.markAllRead')}
                  </button>
                )}
                {hasTrayItems && (
                  <button
                    type="button"
                    onClick={handleClearAll}
                    disabled={clearing}
                    className="min-h-[44px] px-2 text-xs text-white/80 hover:text-white hover:underline touch-manipulation disabled:opacity-50"
                    aria-label={t('layout.clearAllNotifications', 'Clear all notifications')}
                  >
                    {clearing ? t('layout.loading') : t('layout.clearAllNotifications', 'Clear all')}
                  </button>
                )}
              </div>
            )}
          </div>
          <div className="overflow-y-auto flex-1 min-h-0 py-2">
            {loading ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 px-4 py-2">{t('layout.loading')}</p>
            ) : notifications.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 px-4 py-2">{t('layout.noNotifications')}</p>
            ) : (
              <ul className="space-y-0">
                {notifications.map((n) => (
                  <li
                    key={n.id}
                    className={`px-3 py-2 border-b border-gray-100 dark:border-gray-700/60 last:border-b-0 ${!n.is_read ? 'bg-blue-50 dark:bg-blue-900/10' : ''} ${(n.type === 'blog_draft_created' || n.type === 'blog_draft_stale_reminder' || n.type === 'blog_auto_published' || n.type === 'daily_brief') ? 'cursor-pointer hover:bg-blue-100/50 dark:hover:bg-blue-900/20' : ''}`}
                    role={(n.type === 'blog_draft_created' || n.type === 'blog_draft_stale_reminder' || n.type === 'blog_auto_published' || n.type === 'daily_brief') ? 'button' : undefined}
                    tabIndex={(n.type === 'blog_draft_created' || n.type === 'blog_draft_stale_reminder' || n.type === 'blog_auto_published' || n.type === 'daily_brief') ? 0 : undefined}
                    onClick={() => handleNotificationClick(n)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleNotificationClick(n);
                      }
                    }}
                  >
                    <p className="ds-notification-tray__message">{n.message}</p>
                    <p className="ds-notification-tray__meta mt-0.5">
                      {new Date(n.created_at).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })}
                    </p>
                    {!n.is_read && (
                      <button
                        type="button"
                        onClick={() => getCurrentUserId().then((uid) => handleMarkRead(uid, n.id))}
                        className="ds-notification-tray__meta text-[var(--vscode-primary)] hover:underline mt-0.5 touch-manipulation"
                      >
                        {t('layout.markRead')}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// Main Layout component
// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const Layout: React.FC<LayoutProps> = ({
  children,
  currentPage,
  onNavigate,
  onSignOut,
  mainContentPadded = true,
  onHeroSearch,
}) => {
  // â”€â”€ Modal / dialog state â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [showCreditModal, setShowCreditModal] = useState(false);

  // â”€â”€ Profile + role detection â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const [userProfile, setUserProfile] = useState<{ name: string; email: string; avatar_url?: string } | null>(null);
  const [isBlogEditor, setIsBlogEditor] = useState(false);
  const [isOwnerAdmin, setIsOwnerAdmin] = useState(false);

  // â”€â”€ Plan status â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const planStatus = usePlanStatus();
  const credits = planStatus.credits;
  const creditsLoading = planStatus.loading;

  // â”€â”€ Pricing modal trigger event â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const openCreditsUpgrade = useCallback((planId?: string) => {
    openCreditPurchase({
      planId,
      openNativeModal: () => setShowCreditModal(true),
    });
  }, []);

  useEffect(() => {
    const handler = () => {
      if (Capacitor.isNativePlatform()) {
        setShowCreditModal(true);
      } else {
        goToCreditCheckout();
      }
    };
    window.addEventListener('navigate-to-pricing', handler);
    return () => window.removeEventListener('navigate-to-pricing', handler);
  }, []);

  // â”€â”€ Editor mode (`/parsed-resume`) â€” hides chrome â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const [isEditorMode, setIsEditorMode] = useState(
    () => typeof window !== 'undefined' && window.location.pathname.includes('/parsed-resume'),
  );
  useLayoutEffect(() => {
    const handler = (e: Event) => setIsEditorMode((e as CustomEvent<{ active: boolean }>).detail.active);
    window.addEventListener('ggj:editor-mode', handler);
    return () => window.removeEventListener('ggj:editor-mode', handler);
  }, []);

  // â”€â”€ Load user profile (auth + DB overlay) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  useEffect(() => {
    let cancelled = false;

    const loadUserProfile = async () => {
      try {
        const { data: { user: authUser } } = await supabase.auth.getUser();
        if (!authUser || cancelled) return;

        const ur = (authUser.app_metadata?.user_role as string | undefined)
                ?? (authUser.user_metadata?.user_role as string | undefined);
        if (ur === 'editor' || ur === 'admin') setIsBlogEditor(true);

        if (isOwnerAdminEmail(authUser.email)) setIsOwnerAdmin(true);

        const fallbackName =
          authUser.user_metadata?.full_name
          ?? authUser.user_metadata?.name
          ?? (authUser.email ? authUser.email.split('@')[0] : '');
        const fallbackProfile = {
          name: fallbackName || 'User',
          email: authUser.email ?? '',
          avatar_url: authUser.user_metadata?.avatar_url ?? authUser.user_metadata?.picture,
        };
        setUserProfile(fallbackProfile);

        const { data: profile, error } = await supabase
          .from('users')
          .select('name, email, avatar_url')
          .eq('user_id', authUser.id)
          .maybeSingle();

        if (cancelled) return;
        if (error) {
          appLogger.error('Layout: Error fetching profile', error);
          return;
        }
        if (profile && (profile.name?.trim() || profile.email)) {
          setUserProfile({
            name: profile.name?.trim() || fallbackProfile.name,
            email: profile.email ?? fallbackProfile.email,
            avatar_url: profile.avatar_url ?? fallbackProfile.avatar_url,
          });
        }
      } catch (error) {
        appLogger.error('Layout: Exception loading user profile', error);
      }
    };

    loadUserProfile();
    return () => { cancelled = true; };
  }, []);

  // â”€â”€ Reminder sync on mount â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  useEffect(() => {
    getCurrentUserId().then((uid) => {
      if (uid) {
        syncTaskReminderNotifications(uid).catch(() => {});
        syncCalendarReminderNotifications(uid).catch(() => {});
      }
    });
  }, []);

  const handleSignOut = () => setShowLogoutDialog(true);
  const confirmSignOut = async () => {
    setShowLogoutDialog(false);
    await onSignOut();
  };

  // In editor mode we hide chrome â€” shell is replaced with a bare wrapper
  // and a floating utility strip portalled to body.
  if (isEditorMode) {
    return (
      <div className="min-h-dvh vscode-bg-primary vscode-transition" data-tour="dashboard-main">
        <main className="relative flex-1 min-h-0 overflow-hidden flex flex-col vscode-bg-primary vscode-transition pt-3 px-0 pb-0" style={{ pointerEvents: 'auto' }}>
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden dashboard-scroll-root flex flex-col relative z-0" style={{ pointerEvents: 'auto' }}>
            {children}
          </div>
        </main>
        <EditorModeUtilityStrip
          credits={credits}
          creditsLoading={creditsLoading}
          onUpgradeClick={() => openCreditsUpgrade()}
        />
        <LogoutDialog
          isOpen={showLogoutDialog}
          onClose={() => setShowLogoutDialog(false)}
          onConfirm={confirmSignOut}
        />
        {Capacitor.isNativePlatform() && (
          <CreditPurchaseModal
            isOpen={showCreditModal}
            onClose={() => setShowCreditModal(false)}
            onPurchaseComplete={() => { setShowCreditModal(false); planStatus.refresh(); }}
          />
        )}
      </div>
    );
  }

  return (
    <>
        <WorkspaceShell
          currentPage={currentPage}
          onNavChange={onNavigate}
          userName={userProfile?.name ?? null}
          userEmail={userProfile?.email ?? ''}
          userAvatarUrl={userProfile?.avatar_url}
          credits={credits ?? null}
          creditsLoading={creditsLoading}
          onUpgradeClick={() => openCreditsUpgrade()}
          onSignOutClick={handleSignOut}
          isBlogEditor={isBlogEditor}
          isOwnerAdmin={isOwnerAdmin}
          onHeroSearch={onHeroSearch}
          fullBleed={currentPage === 'agent' || currentPage === 'account-settings' || currentPage === 'blog-cms'}
        >
          {(currentPage === 'agent' || currentPage === 'account-settings' || currentPage === 'blog-cms') ? (
            children
          ) : currentPage === 'dashboard' ? (
            <div className="flex-1 min-h-0 h-full overflow-hidden flex flex-col px-2 sm:px-3 lg:px-4">
              {children}
            </div>
          ) : mainContentPadded ? (
            <div className="px-2 sm:px-3 lg:px-4 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
              {children}
            </div>
          ) : (
            <div className="pb-[max(0.5rem,env(safe-area-inset-bottom))]">{children}</div>
          )}
        </WorkspaceShell>

        <LogoutDialog
          isOpen={showLogoutDialog}
          onClose={() => setShowLogoutDialog(false)}
          onConfirm={confirmSignOut}
        />
        {Capacitor.isNativePlatform() && (
          <CreditPurchaseModal
            isOpen={showCreditModal}
            onClose={() => setShowCreditModal(false)}
            onPurchaseComplete={() => { setShowCreditModal(false); planStatus.refresh(); }}
          />
        )}
    </>
  );
};


// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// Editor-mode utility strip (used when /parsed-resume is up)
// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
function EditorModeUtilityStrip({
  credits,
  creditsLoading,
  onUpgradeClick,
}: {
  credits: number | null | undefined;
  creditsLoading: boolean;
  onUpgradeClick: () => void;
}) {
  const { t } = useTranslation();
  return createPortal(
    <div
      className="fixed top-3 right-0 z-[9990] flex items-center gap-0.5 px-2 py-1 pointer-events-auto
        [&_button]:bg-transparent [&_button]:border-0 [&_button]:shadow-none
        [&_button]:rounded-lg [&_button]:hover:bg-black/5 [&_button]:dark:hover:bg-white/10
        [&_button]:transition-colors"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        onClick={onUpgradeClick}
        className="flex items-center gap-1 min-h-[32px] px-2 text-xs font-semibold touch-manipulation vscode-text-primary"
        title={t('layout.profile.getMore')}
      >
        <Coins className="w-3.5 h-3.5 text-cyan-500 dark:text-cyan-400 shrink-0" />
        <span>
          {creditsLoading
            ? <span className="inline-block w-5 h-3 rounded animate-pulse bg-current opacity-30" aria-hidden="true" />
            : (credits ?? 0)}
        </span>
      </button>
      <NotificationTrayButton onNavigate={onNavigate} />
      <LanguageSelector variant="compact" />
      <ThemeToggleButton className="shrink-0" />
    </div>,
    document.body,
  );
}


export default React.memo(Layout);
